import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useComponentDefaults } from "../../Provider";
import { clsx } from "../../utils/clsx";
import s from "./Popover.module.css";

const PLACEMENTS = [
  "top","bottom","left","right",
  "topLeft","topRight","bottomLeft","bottomRight",
  "leftTop","leftBottom","rightTop","rightBottom"
];

export default function Popover(props) {
  const merged = useComponentDefaults('Popover', props, {
    arrow: false,
    autoAdjustOverflow: true,
    defaultOpen: false,
    destroyOnHidden: false,
    fresh: false,
    mouseEnterDelay: 0.1,
    mouseLeaveDelay: 0.1,
    placement: 'top',
    trigger: 'click',
    closeOnBlur: true,
    closeOnContentClick: false,
  });
  const {
    title,
    content,
    children,
    className,
    contentClassName,
    contentStyle,
    surfaceStyle,
    arrow = true,
    autoAdjustOverflow = true,
    color,
    defaultOpen = false,
    destroyOnHidden = false,
    getPopupContainer,
    mouseEnterDelay = 0.1,
    mouseLeaveDelay = 0.1,
    placement = 'top',
    trigger: triggerProp = 'click',
    closeOnBlur = true,
    closeOnContentClick = false,
    open: openProp,
    zIndex,
    style: wrapperStyle,
    onOpenChange,
  } = merged;
  const wrapRef = useRef(null);
  const popRef = useRef(null);

  const [internalOpen, setInternalOpen] = useState(!!defaultOpen);
  const isControlled = typeof openProp === "boolean";
  const open = isControlled ? !!openProp : internalOpen;

  const [curPlacement, setCurPlacement] = useState(PLACEMENTS.includes(placement) ? placement : "top");
  const enterTimer = useRef();
  const leaveTimer = useRef();

  // Determine trigger element vs modes (back-compat)
  const triggerModes = useMemo(() => {
    const isElement = React.isValidElement(triggerProp) || (typeof triggerProp !== 'string' && !Array.isArray(triggerProp));
    if (isElement) return ['click'];
    const t = triggerProp;
    if (Array.isArray(t)) return t;
    return [String(t || 'click')];
  }, [triggerProp]);
  const triggerChild = useMemo(() => {
    const isElement = React.isValidElement(triggerProp) || (typeof triggerProp !== 'string' && !Array.isArray(triggerProp));
    return children || (isElement ? triggerProp : null);
  }, [children, triggerProp]);

  const container = (() => {
    if (typeof document === "undefined") return null;
    try {
      if (typeof getPopupContainer === "function") return getPopupContainer(wrapRef.current) || document.body;
    } catch {}
    return document.body;
  })();

  const setOpen = useCallback((value) => {
    const nextOpen = Boolean(value);
    if (nextOpen === open) return;
    if (!isControlled) setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  }, [isControlled, onOpenChange, open]);

  const clearTimers = () => {
    window.clearTimeout(enterTimer.current);
    window.clearTimeout(leaveTimer.current);
  };

  const handleEnter = () => { clearTimers(); enterTimer.current = window.setTimeout(() => setOpen(true), mouseEnterDelay * 1000); };
  const handleLeave = () => { clearTimers(); leaveTimer.current = window.setTimeout(() => setOpen(false), mouseLeaveDelay * 1000); };
  const handleClick = (event) => {
    // React events from portaled popover content still bubble through the
    // trigger's React tree. Ignore those events so interacting with content
    // does not accidentally toggle the trigger closed.
    if (popRef.current?.contains(event.target)) return;
    setOpen(!open);
  };
  const handleContextMenu = (e) => { e.preventDefault(); setOpen(true); };
  const handleFocus = () => setOpen(true);
  const handleBlur = () => setOpen(false);

  const computePositionNow = useCallback(() => {
    const host = wrapRef.current;
    const pop = popRef.current;
    if (!host || !pop) return;

    const rect = host.getBoundingClientRect();
    const pRect = pop.getBoundingClientRect();
    const margin = 8;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

    const toTitleCase = (value) => (value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : '');
    const resolvePlacement = (value) => (PLACEMENTS.includes(value) ? value : 'top');
    const clamp = (val, min, max) => {
      if (Number.isNaN(val)) return min;
      if (min > max) return Math.min(Math.max(val, max), min);
      return Math.min(Math.max(val, min), max);
    };

    const computeForPlacement = (targetPlacement) => {
      const nextPlacement = resolvePlacement(targetPlacement);
      const primary = nextPlacement.split(/[A-Z]/)[0];
      const alignPart = nextPlacement.replace(primary, '').toLowerCase();
      const centerX = rect.left + rect.width / 2 - pRect.width / 2;
      const centerY = rect.top + rect.height / 2 - pRect.height / 2;
      let top = centerY;
      let left = centerX;

      if (primary === 'top') {
        top = rect.top - pRect.height - margin;
        left = centerX;
        if (alignPart === 'left') left = rect.left;
        if (alignPart === 'right') left = rect.right - pRect.width;
      } else if (primary === 'bottom') {
        top = rect.bottom + margin;
        left = centerX;
        if (alignPart === 'left') left = rect.left;
        if (alignPart === 'right') left = rect.right - pRect.width;
      } else if (primary === 'left') {
        left = rect.left - pRect.width - margin;
        top = centerY;
        if (alignPart === 'top') top = rect.top;
        if (alignPart === 'bottom') top = rect.bottom - pRect.height;
      } else if (primary === 'right') {
        left = rect.right + margin;
        top = centerY;
        if (alignPart === 'top') top = rect.top;
        if (alignPart === 'bottom') top = rect.bottom - pRect.height;
      }

      return { top, left, primary, alignPart, placementKey: nextPlacement };
    };

    const getOpposite = (primary) => {
      switch (primary) {
        case 'top':
          return 'bottom';
        case 'bottom':
          return 'top';
        case 'left':
          return 'right';
        case 'right':
          return 'left';
        default:
          return primary;
      }
    };

    const getPrimaryOverflow = (position) => {
      const { top, left, primary: posPrimary } = position;
      if (posPrimary === 'top') return Math.max(margin - top, 0);
      if (posPrimary === 'bottom') return Math.max(top + pRect.height - (viewportHeight - margin), 0);
      if (posPrimary === 'left') return Math.max(margin - left, 0);
      if (posPrimary === 'right') return Math.max(left + pRect.width - (viewportWidth - margin), 0);
      return 0;
    };

    const clampToViewport = (position) => {
      const maxLeft = Math.max(viewportWidth - pRect.width - margin, margin);
      const maxTop = Math.max(viewportHeight - pRect.height - margin, margin);
      const minLeft = Math.min(margin, maxLeft);
      const minTop = Math.min(margin, maxTop);
      return {
        ...position,
        top: clamp(position.top, minTop, maxTop),
        left: clamp(position.left, minLeft, maxLeft),
      };
    };

    let current = computeForPlacement(placement);

    if (autoAdjustOverflow) {
      const overflow = getPrimaryOverflow(current);
      if (overflow > 0) {
        const oppositePrimary = getOpposite(current.primary);
        const flippedPlacement = `${oppositePrimary}${toTitleCase(current.alignPart)}`;
        const flipped = computeForPlacement(flippedPlacement);
        const flippedOverflow = getPrimaryOverflow(flipped);
        if (flippedOverflow < overflow) {
          current = flipped;
        }
      }
      current = clampToViewport(current);
    }

    pop.style.top = `${Math.round(current.top)}px`;
    pop.style.left = `${Math.round(current.left)}px`;
    setCurPlacement(current.placementKey);
  }, [placement, autoAdjustOverflow]);

  const computePosition = useCallback(() => {
    // Wait a frame so portaled node has dimensions
    requestAnimationFrame(() => computePositionNow());
  }, [computePositionNow]);

  useEffect(() => {
    if (!open) return;
    computePosition();
    const handler = () => computePosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open, computePosition]);

  useEffect(() => () => { clearTimers(); }, []);

  const triggerProps = {};
  if (triggerModes.includes('hover')) { triggerProps.onMouseEnter = handleEnter; triggerProps.onMouseLeave = handleLeave; }
  if (triggerModes.includes('click')) {
    triggerProps.onClick = handleClick;
    triggerProps.onKeyDown = (e) => { if (e.key === 'Escape') setOpen(false); };
    if (closeOnBlur) triggerProps.onBlur = () => setOpen(false);
  }
  if (triggerModes.includes('focus')) {
    triggerProps.onFocus = handleFocus;
    if (closeOnBlur) triggerProps.onBlur = handleBlur;
  }
  if (triggerModes.includes('contextMenu')) { triggerProps.onContextMenu = handleContextMenu; }
  if (closeOnBlur && !triggerProps.onBlur) triggerProps.onBlur = handleBlur;

  // Click-away close
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const t = e.target;
      const host = wrapRef.current, pop = popRef.current;
      if (!host || !pop) return;
      if (host.contains(t) || pop.contains(t)) return;
      setOpen(false);
    };
    const onFocusIn = (e) => {
      const t = e.target;
      const host = wrapRef.current, pop = popRef.current;
      if (!host || !pop) return;
      if (host.contains(t) || pop.contains(t)) return;
      setOpen(false);
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc, true);
    if (closeOnBlur) {
      document.addEventListener('focusin', onFocusIn, true);
    }
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      if (closeOnBlur) {
        document.removeEventListener('focusin', onFocusIn, true);
      }
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open, closeOnBlur, setOpen]);

  // Color tokens: support token name or var(...)
  const { bgVar, fgVar } = useMemo(() => {
    if (!color) return { bgVar: undefined, fgVar: undefined };
    let varRef;
    if (typeof color === 'string' && color.startsWith('var(')) varRef = color; else varRef = `var(--${color.trim()})`;
    let fg;
    const m = typeof color === 'string' && color.match(/^(gray|sky)-(\d{1,2})$/);
    if (m) { const idx = parseInt(m[2], 10); fg = idx <= 5 ? 'var(--text)' : '#fff'; }
    else if (typeof color === 'string' && /^(brand|accent-|info|success|warning|error)/.test(color)) { fg = '#fff'; }
    return { bgVar: varRef, fgVar: fg };
  }, [color]);

  const titleNode = typeof title === 'function' ? title() : title;
  const contentNode = typeof content === 'function' ? content() : content;

  const popNode = (
    <div
      ref={popRef}
      className={clsx(s.pop)}
      role="dialog"
      data-hidden={!open}
      data-placement={curPlacement}
      data-arrow={arrow ? 'true' : 'false'}
      onClick={closeOnContentClick ? () => setOpen(false) : undefined}
      style={{
        '--pop-bg': bgVar || undefined,
        '--pop-fg': fgVar || undefined,
        '--pop-z': zIndex || undefined,
        ...surfaceStyle,
      }}
    >
      {arrow ? <span className={s.arrow} aria-hidden /> : null}
      {titleNode ? <h4 className={s.title}>{titleNode}</h4> : null}
      <div className={contentClassName} style={contentStyle}>{contentNode}</div>
    </div>
  );

  return (
    <span ref={wrapRef} className={clsx(s.wrap, className)} style={wrapperStyle} {...triggerProps}>
      {triggerChild}
      {(!open && destroyOnHidden) ? null : (container ? createPortal(popNode, container) : popNode)}
    </span>
  );
}
